import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ButtonClick, NotificationService, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import { first, Observable, Subject, Subscription, takeUntil } from 'rxjs';

import { selectSeReportImagesPerRowInPlaceholder, selectSeReportIsPreview, selectSeReportKeyImages } from '../../../report/report.selectors';
import { KeyImage } from '../../../services/imaging/common';
import { SeSideComponentBase, SeSideComponentType } from '../se-side-components-models';
import { ISeSideKeyObject, ISeSideKeyObjectImage, SeSideImagesType } from './se-side-key-images-models';
import { KeyImagesService } from './key-images.service';
import { seReportSetIsModified } from '../../../report/report.actions';

@Component({
    selector: 'se-side-key-images-container',
    templateUrl: './se-side-key-images-container.component.html',
    styleUrls: ['./se-side-key-images-container.component.scss'],
})

export class SeSideKeyImagesContainerComponent extends SeSideComponentBase implements OnInit, OnDestroy {

    isPreview: boolean = false;
    layoutNumber: number = 2;
    keyImages: ISeSideKeyObjectImage[] = [];
    sideKeyImagesMessage: string = null;
    type: SeSideComponentType = SeSideComponentType.keyImagesPreview;

    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private storeSubscription: Subscription;
    private imageForRowSubscription: Subscription;

    constructor(private keyImagesService: KeyImagesService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translateService: TranslateService,
        private notificationService: NotificationService,
        private store: Store) {
        super();
        this.sideKeyImagesMessage = this.translateService.instant('NoResults');
        this.isLoading.next(false);
    }

    ngOnInit(): void {
        this.listenForIsPreviewEvent();
        this.listenForStoreEvents();
    }

    ngOnDestroy(): void {
        this.storeSubscription?.unsubscribe();
        this.imageForRowSubscription?.unsubscribe();
        this.ngUnsubscribe.next();
    }

    private listenForIsPreviewEvent() {
        this.store.pipe(select(selectSeReportIsPreview)).subscribe(
            (isPreview: boolean) => {
                this.isPreview = isPreview;
            }
        )
    }

    private listenForStoreEvents() {
        this.storeSubscription = this.store.pipe(
            select(selectSeReportKeyImages)).subscribe(
                async (metaData: KeyImage[]) => {
                    this.isLoading.next(true);
                    const notAvailableMsg = this.translateService.instant('KeyImagesServiceNotAvailable');
                    const noResultMsg = this.translateService.instant('NoResults');
                    if (this.isPreview) {
                        this.keyImages = [await import('./mock-data.json')];
                        this.isLoading.next(false);
                    } else {
                        try {
                            if (!!metaData && metaData.length > 0) {
                                this.resolveKeyImagesServiceCall(metaData, noResultMsg, notAvailableMsg)
                            } else {
                                this.keyImages = [];
                                this.sideKeyImagesMessage = noResultMsg;
                                this.isLoading.next(false);
                            }
                        }
                        catch (error) {
                            this.sideKeyImagesMessage = noResultMsg;

                            // Notifica warning
                            this.notificationService.warning(
                                (error && error.message) ? error.message : '', notAvailableMsg);

                            this.isLoading.next(false);
                        }
                    }
                }
        );

        this.imageForRowSubscription = this.store.pipe(
            select(selectSeReportImagesPerRowInPlaceholder)).subscribe(
                (n) => this.layoutNumber = n );
    }

    getDataByObjectKey(image: SeSideImagesType, metaData: KeyImage[]): KeyImage {
        return metaData.find((img) =>
            img.IdExamination === image.idExamination
            && img.RepositoryKey === image.repositoryKey
            && img.SOPInstanceUID === image.sopInstanceUID
        );
    }

    populateObject(images: SeSideImagesType[], metaData: KeyImage[]) {
        if (!images) {
            this.keyImages = [];
            return;
        }

        this.keyImages = images.map((image: SeSideImagesType) => {
            const img: KeyImage = this.getDataByObjectKey(image, metaData);
            if (img) {
                return {
                    Image: image.data,
                    Caption: img.Caption,
                    ExcludedFromReport: img.ExcludedFromReport,
                    IdExamination: img.IdExamination,
                    RepositoryKey: img.RepositoryKey,
                    SOPInstanceUID: img.SOPInstanceUID,
                    IsDeletable: img.IsDeletable
                }
            }
        });
        this.sideKeyImagesMessage = null;
    }

    resolveKeyImagesServiceCall(metaData: KeyImage[], noResultMsg: string, notAvailableMsg: string) {
        const request: ISeSideKeyObject[] = metaData.map((elem: KeyImage) => {
            return {
                RepositoryKey: elem.RepositoryKey,
                SOPInstanceUID: elem.SOPInstanceUID,
                IdExamination: elem.IdExamination
            }
        });
        this.keyImagesService.getKeyImages(request)
            .pipe(takeUntil(this.ngUnsubscribe)) // blocca le eventuali chiamate in corso
            .subscribe({
                next: (result) => {
                    this.populateObject(result, metaData);
                    this.isLoading.next(false);
                },
                error: (error) => {
                    this.sideKeyImagesMessage = noResultMsg;
                    this.notificationService.warning(
                        (error.statusText) ? error.statusText : '', notAvailableMsg);
                    this.isLoading.next(false);
                }
            })
    }

    updateState(event: KeyImage) {
        const convertedObj: KeyImage[] = this.keyImages.map(({ Image: _, ...rest }) => rest);

        let updatedObj: KeyImage[] = convertedObj.map(obj =>
            obj.IdExamination === event.IdExamination
                && obj.RepositoryKey === event.RepositoryKey
                && obj.SOPInstanceUID === event.SOPInstanceUID ? event : obj);

        this.keyImagesService.saveKeyImagesData(updatedObj);
    }

    showModal(image: ISeSideKeyObjectImage) {
        this.modalDialogNotificationService.notify({ message: this.translateService.instant('ImageKey_DeleteConfirmationMessage'), title: this.translateService.instant('Warning'), type: NotificationType.information, buttonYes: true, buttonNo: true, buttonOk: false, buttonCancel: false, }).pipe(first()).subscribe((button) => {
            if (button === ButtonClick.yes) {
                this.keyImagesService.requestKeyImageDeletion(image)
            }
        });
    }

    deleteImagesFromRepository() {
        this.modalDialogNotificationService.notify({ message: this.translateService.instant('ImageKey_DeleteAllConfirmationMessage'), title: this.translateService.instant('Warning'), type: NotificationType.information, buttonYes: true, buttonNo: true, buttonOk: false, buttonCancel: false, }).pipe(first()).subscribe((button) => {
            if (button === ButtonClick.yes) {
                this.keyImagesService.requestAllKeyImagesDeletion(this.keyImages);
            }
        });

    }

    selectRow(number: number) {
        this.keyImagesService.saveRowValue(number);
        this.store.dispatch(seReportSetIsModified({ isModified: true }));
    }
}
